import React from 'react';
import { Box, Typography, Button, IconButton, Divider } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import CustomInput from '@/components/CustomInput/CustomInput';
import {
  isFieldVisible,
  getFieldLabel,
  getAutoFillDefaults,
  snakeToCamel,
} from '@/utils/fieldConfig';
import ConditionalInput from '@/views/Facilities/CreateOrEditManualRefund/ConditionalInput';

const helperTextStyle = {
  fontWeight: 'bold',
  fontSize: '0.9em',
};

const LineItemsSection = ({
  lineItems,
  onChange,
  disabled,
  errors,
  refundModalType,
  customPaymentType,
}) => {
  const { t } = useTranslation();
  const lineItemFields =
    customPaymentType?.createRefundConfig
      ?.filter((config) => config.field?.startsWith('lineItem.'))
      .map((config) => config.field) || [];

  const hasVisibleFields = lineItemFields.some((fieldName) =>
    isFieldVisible(fieldName, refundModalType, customPaymentType),
  );

  if (!hasVisibleFields) {
    return null;
  }

  const getLineItemError = (index, field) => {
    const key = `lineItems[${index}].${snakeToCamel(field)}`;
    return errors?.[key];
  };

  const lineItemErrorProps = (index, field) => {
    const err = getLineItemError(index, field);
    return {
      error: !!err?.messages?.length,
      inputProps: { helperText: err?.messages?.join(', ') },
      helperTextStyle,
    };
  };

  const handleLineItemChange = (index, field, value) => {
    const updatedLineItems = [...lineItems];
    updatedLineItems[index] = {
      ...updatedLineItems[index],
      [field]: value,
    };
    onChange(updatedLineItems);
  };

  const handleAddLineItem = () => {
    const newLineItem = {
      gl_code_1: '',
      gl_code_2: '',
      gl_code_3: '',
      gl_code_4: '',
      gl_code_5: '',
      memo: '',
      amount: '',
      identifier: '',
      internal_comment: '',
      ...getAutoFillDefaults(customPaymentType, 'lineItem'),
    };
    onChange([...lineItems, newLineItem]);
  };

  const handleRemoveLineItem = (index) => {
    const updatedLineItems = lineItems.filter((_, i) => i !== index);
    onChange(updatedLineItems);
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}>
        <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
          {t('lineItems.title')}
        </Typography>
        <Button
          variant='contained'
          color='primary'
          startIcon={<Add />}
          onClick={handleAddLineItem}
          disabled={disabled}
          size='small'
          sx={{ color: 'white', '& .MuiSvgIcon-root': { color: 'white' } }}>
          {t('lineItems.addButton')}
        </Button>
      </Box>

      {lineItems.length === 0 ? (
        <Typography variant='body2' color='text.secondary' sx={{ py: 2 }}>
          {t('lineItems.emptyMessage')}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {lineItems.map((lineItem, index) => (
            <Box
              key={index}
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                p: 2,
                position: 'relative',
                backgroundColor: '#fafafa',
              }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}>
                <Typography variant='subtitle2' sx={{ fontWeight: 'bold' }}>
                  {t('lineItems.itemLabel', { number: index + 1 })}
                </Typography>
                <IconButton
                  onClick={() => handleRemoveLineItem(index)}
                  color='error'
                  disabled={disabled}
                  size='small'>
                  <Delete />
                </IconButton>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <GridContainer>
                <ConditionalInput
                  fieldName='lineItem.gl_code_1'
                  refundModalType={refundModalType}
                  customPaymentType={customPaymentType}>
                  <GridItem xs={12} sm={6} md={4}>
                    <CustomInput
                      labelText={getFieldLabel(
                        t('lineItems.fields.glCode1'),
                        'lineItem.gl_code_1',
                        customPaymentType,
                      )}
                      id={`gl_code_1_${index}`}
                      name={`gl_code_1_${index}`}
                      value={lineItem.gl_code_1 || ''}
                      onChange={(e) =>
                        handleLineItemChange(index, 'gl_code_1', e.target.value)
                      }
                      formControlProps={{ fullWidth: true }}
                      disabled={disabled}
                      {...lineItemErrorProps(index, 'gl_code_1')}
                    />
                  </GridItem>
                </ConditionalInput>
                <ConditionalInput
                  fieldName='lineItem.gl_code_2'
                  refundModalType={refundModalType}
                  customPaymentType={customPaymentType}>
                  <GridItem xs={12} sm={6} md={4}>
                    <CustomInput
                      labelText={getFieldLabel(
                        t('lineItems.fields.glCode2'),
                        'lineItem.gl_code_2',
                        customPaymentType,
                      )}
                      id={`gl_code_2_${index}`}
                      name={`gl_code_2_${index}`}
                      value={lineItem.gl_code_2 || ''}
                      onChange={(e) =>
                        handleLineItemChange(index, 'gl_code_2', e.target.value)
                      }
                      formControlProps={{ fullWidth: true }}
                      disabled={disabled}
                      {...lineItemErrorProps(index, 'gl_code_2')}
                    />
                  </GridItem>
                </ConditionalInput>
                <ConditionalInput
                  fieldName='lineItem.gl_code_3'
                  refundModalType={refundModalType}
                  customPaymentType={customPaymentType}>
                  <GridItem xs={12} sm={6} md={4}>
                    <CustomInput
                      labelText={getFieldLabel(
                        t('lineItems.fields.glCode3'),
                        'lineItem.gl_code_3',
                        customPaymentType,
                      )}
                      id={`gl_code_3_${index}`}
                      name={`gl_code_3_${index}`}
                      value={lineItem.gl_code_3 || ''}
                      onChange={(e) =>
                        handleLineItemChange(index, 'gl_code_3', e.target.value)
                      }
                      formControlProps={{ fullWidth: true }}
                      disabled={disabled}
                      {...lineItemErrorProps(index, 'gl_code_3')}
                    />
                  </GridItem>
                </ConditionalInput>
                <ConditionalInput
                  fieldName='lineItem.gl_code_4'
                  refundModalType={refundModalType}
                  customPaymentType={customPaymentType}>
                  <GridItem xs={12} sm={6} md={4}>
                    <CustomInput
                      labelText={getFieldLabel(
                        t('lineItems.fields.glCode4'),
                        'lineItem.gl_code_4',
                        customPaymentType,
                      )}
                      id={`gl_code_4_${index}`}
                      name={`gl_code_4_${index}`}
                      value={lineItem.gl_code_4 || ''}
                      onChange={(e) =>
                        handleLineItemChange(index, 'gl_code_4', e.target.value)
                      }
                      formControlProps={{ fullWidth: true }}
                      disabled={disabled}
                      {...lineItemErrorProps(index, 'gl_code_4')}
                    />
                  </GridItem>
                </ConditionalInput>
                <ConditionalInput
                  fieldName='lineItem.gl_code_5'
                  refundModalType={refundModalType}
                  customPaymentType={customPaymentType}>
                  <GridItem xs={12} sm={6} md={4}>
                    <CustomInput
                      labelText={getFieldLabel(
                        t('lineItems.fields.glCode5'),
                        'lineItem.gl_code_5',
                        customPaymentType,
                      )}
                      id={`gl_code_5_${index}`}
                      name={`gl_code_5_${index}`}
                      value={lineItem.gl_code_5 || ''}
                      onChange={(e) =>
                        handleLineItemChange(index, 'gl_code_5', e.target.value)
                      }
                      formControlProps={{ fullWidth: true }}
                      disabled={disabled}
                      {...lineItemErrorProps(index, 'gl_code_5')}
                    />
                  </GridItem>
                </ConditionalInput>
                <ConditionalInput
                  fieldName='lineItem.amount'
                  refundModalType={refundModalType}
                  customPaymentType={customPaymentType}>
                  <GridItem xs={12} sm={6} md={4}>
                    <CustomInput
                      labelText={getFieldLabel(
                        t('lineItems.fields.amount'),
                        'lineItem.amount',
                        customPaymentType,
                      )}
                      id={`amount_${index}`}
                      name={`amount_${index}`}
                      value={lineItem.amount || ''}
                      onChange={(e) =>
                        handleLineItemChange(index, 'amount', e.target.value)
                      }
                      formControlProps={{ fullWidth: true }}
                      disabled={disabled}
                      {...lineItemErrorProps(index, 'amount')}
                    />
                  </GridItem>
                </ConditionalInput>
                <ConditionalInput
                  fieldName='lineItem.identifier'
                  refundModalType={refundModalType}
                  customPaymentType={customPaymentType}>
                  <GridItem xs={12} sm={6} md={6}>
                    <CustomInput
                      labelText={getFieldLabel(
                        t('lineItems.fields.identifier'),
                        'lineItem.identifier',
                        customPaymentType,
                      )}
                      id={`identifier_${index}`}
                      name={`identifier_${index}`}
                      value={lineItem.identifier || ''}
                      onChange={(e) =>
                        handleLineItemChange(
                          index,
                          'identifier',
                          e.target.value,
                        )
                      }
                      formControlProps={{ fullWidth: true }}
                      disabled={disabled}
                      {...lineItemErrorProps(index, 'identifier')}
                    />
                  </GridItem>
                </ConditionalInput>
                <ConditionalInput
                  fieldName='lineItem.memo'
                  refundModalType={refundModalType}
                  customPaymentType={customPaymentType}>
                  <GridItem xs={12} sm={6} md={6}>
                    <CustomInput
                      labelText={getFieldLabel(
                        t('lineItems.fields.memo'),
                        'lineItem.memo',
                        customPaymentType,
                      )}
                      id={`memo_${index}`}
                      name={`memo_${index}`}
                      value={lineItem.memo || ''}
                      onChange={(e) =>
                        handleLineItemChange(index, 'memo', e.target.value)
                      }
                      formControlProps={{ fullWidth: true }}
                      disabled={disabled}
                      {...lineItemErrorProps(index, 'memo')}
                    />
                  </GridItem>
                </ConditionalInput>
                <ConditionalInput
                  fieldName='lineItem.internal_comment'
                  refundModalType={refundModalType}
                  customPaymentType={customPaymentType}>
                  <GridItem xs={12} sm={12} md={12}>
                    <CustomInput
                      labelText={getFieldLabel(
                        t('lineItems.fields.internalComment'),
                        'lineItem.internal_comment',
                        customPaymentType,
                      )}
                      id={`internal_comment_${index}`}
                      name={`internal_comment_${index}`}
                      value={lineItem.internal_comment || ''}
                      onChange={(e) =>
                        handleLineItemChange(
                          index,
                          'internal_comment',
                          e.target.value,
                        )
                      }
                      formControlProps={{ fullWidth: true }}
                      disabled={disabled}
                      {...lineItemErrorProps(index, 'internal_comment')}
                    />
                  </GridItem>
                </ConditionalInput>
              </GridContainer>
            </Box>
          ))}
        </Box>
      )}

      {errors?.lineItems?.messages?.length > 0 && (
        <Typography color='error' sx={{ mt: 1, fontWeight: 'bold' }}>
          {errors.lineItems.messages.join(', ')}
        </Typography>
      )}
    </Box>
  );
};

export default LineItemsSection;
